import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import { Box, Button, Field, Flex, Text } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Trash } from 'iconoir-react';

interface AvatarUploadProps {
  initialImageUrl?: string;
  onImageUploaded?: (imageUrl: string) => void;
  size?: 'sm' | 'md' | 'lg';
  endpoint?: string;
  deleteEndpoint?: string;
  queryKey?: string[];
  customUploadFn?: (file: File) => Promise<string>;
  customDeleteFn?: () => Promise<void>;
  fieldName?: string;
  responseUrlField?: string;
  label?: string;
}

// Function to upload avatar
const uploadAvatar = async (
  file: File,
  endpoint: string,
  fieldName: string = 'file',
  responseUrlField: string = 'fileUrl'
): Promise<string> => {
  const formData = new FormData();
  formData.append(fieldName, file);

  const response = await api.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data[responseUrlField];
};

// Function to delete avatar
const deleteAvatar = async (endpoint: string): Promise<void> => {
  await api.delete(endpoint);
};

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  initialImageUrl,
  onImageUploaded,
  size = 'md',
  endpoint = '/users/avatar',
  deleteEndpoint = '/users/avatar',
  queryKey = ['user'],
  customUploadFn,
  customDeleteFn,
  fieldName = 'file',
  responseUrlField = 'fileUrl',
  label = 'Avatar',
}) => {
  const [imageUrl, setImageUrl] = useState<string | undefined>(initialImageUrl);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { refreshCurrentUser: fetchCurrentUser } = useAuth();
  const { t } = useTranslation();

  // Size mapping
  const sizeMap = {
    sm: { width: '64px', height: '64px' },
    md: { width: '88px', height: '88px' },
    lg: { width: '120px', height: '120px' },
  };

  // Use React Query for the upload mutation
  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      customUploadFn
        ? customUploadFn(file)
        : uploadAvatar(file, endpoint, fieldName, responseUrlField),
    onSuccess: async (newImageUrl) => {
      setImageUrl(newImageUrl);
      onImageUploaded?.(newImageUrl);

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey });
      await fetchCurrentUser();
      toaster.success({
        title: t('settings.avatarUpload.successUploaded'),
      });
    },
    onError: (error) => {
      const errorMessage = getApiErrorMessage(error, t, 'settings.avatarUpload.errorUploadFailed');
      setError(errorMessage);
      console.error('Upload error:', error);
      toaster.error({
        title: t('settings.avatarUpload.errorUploadFailed'),
        description: errorMessage,
      });
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!validateFile(file)) return;

    uploadMutation.mutate(file);
  };

  const validateFile = (file: File): boolean => {
    const isImage = file.type.startsWith('image/');
    const maxSizeMB = 5;
    const isLt5M = file.size / 1024 / 1024 < maxSizeMB;

    if (!isImage) {
      setError(t('settings.avatarUpload.errorImageOnly'));
      return false;
    }

    if (!isLt5M) {
      setError(t('settings.avatarUpload.errorFileSize', { maxSizeMB }));
      return false;
    }

    setError(null);
    return true;
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const deleteMutation = useMutation({
    mutationFn: () => (customDeleteFn ? customDeleteFn() : deleteAvatar(deleteEndpoint)),
    onSuccess: async () => {
      setImageUrl(undefined);
      onImageUploaded?.('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey });
      await fetchCurrentUser();
      toaster.success({
        title: t('settings.avatarUpload.successDeleted'),
      });
    },
    onError: (error) => {
      const errorMessage = getApiErrorMessage(error, t, 'settings.avatarUpload.errorDeleteFailed');
      console.error('Delete error:', error);
      toaster.error({
        title: t('settings.avatarUpload.errorDeleteFailed'),
        description: errorMessage,
      });
    },
  });

  const handleRemove = () => {
    if (imageUrl) {
      deleteMutation.mutate();
    } else {
      // If there's no image, just reset the state
      setImageUrl(undefined);
      onImageUploaded?.('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Field.Root invalid={!!error}>
      <Flex gap={6} alignItems="center">
        {/* Avatar (non-clickable) */}
        <Box
          position="relative"
          width={sizeMap[size].width}
          height={sizeMap[size].height}
          rounded="full"
          overflow="hidden"
        >
          {/* Image container */}
          {imageUrl ? (
            <Box
              backgroundImage={`url(${imageUrl})`}
              backgroundSize="cover"
              backgroundPosition="center"
              width="100%"
              height="100%"
            />
          ) : (
            <Flex
              width="100%"
              height="100%"
              bg="gray.200"
              align="center"
              justify="center"
              direction="column"
            >
              <Box as="span" display="inline-flex" color="black/60">
                <Camera width={24} height={24} />
              </Box>
            </Flex>
          )}
        </Box>

        {/* Upload button and helper text */}
        <Flex direction="column" gap={2} alignItems="flex-start">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: 'none' }}
          />
          <Flex gap={2}>
            <Button
              onClick={handleUploadClick}
              variant="subtle"
              loading={uploadMutation.isPending}
              loadingText={t('settings.avatarUpload.uploading')}
              disabled={uploadMutation.isPending || deleteMutation.isPending}
              rounded="full"
              size="sm"
              minWidth="130px"
            >
              <Flex align="center">
                {imageUrl
                  ? `${t('settings.avatarUpload.change')} ${label}`
                  : `${t('settings.avatarUpload.upload')} ${label}`}
              </Flex>
            </Button>
            <Button
              variant="outline"
              rounded="full"
              size="sm"
              onClick={handleRemove}
              loading={deleteMutation.isPending}
              loadingText={t('settings.avatarUpload.deleting')}
              disabled={uploadMutation.isPending || deleteMutation.isPending}
              minWidth="120px"
            >
              <Trash />
              {t('settings.avatarUpload.delete')}
            </Button>
          </Flex>
          <Text fontSize="xs">{t('settings.avatarUpload.helperText', { maxSizeMB: 5 })}</Text>
        </Flex>
      </Flex>

      {error && <Field.ErrorText mt={2}>{error}</Field.ErrorText>}
    </Field.Root>
  );
};
